/**
 * Admin Dashboard
 * List all assessments with management options
 */

import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { assessmentsApi } from '../api/assessments.js';
import { Assessment, PaginatedResponse } from '../../types/models';

export const AdminDashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const searchTerm = searchParams.get('search') || '';
  const statusFilter = searchParams.get('status') || 'all';
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  const setSearchTerm = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set('search', value); else params.delete('search');
    setSearchParams(params, { replace: true });
  };

  const setStatusFilter = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value && value !== 'all') params.set('status', value); else params.delete('status');
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    loadAssessments();
  }, [statusFilter, debouncedSearchTerm]);

  const loadAssessments = async () => {
    try {
      if (assessments.length === 0) setLoading(true);
      const result: PaginatedResponse<Assessment> = await assessmentsApi.list({
        status: statusFilter !== 'all' ? statusFilter as any : undefined,
        search: debouncedSearchTerm || undefined,
        limit: 100,
      });
      setAssessments(result.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (id: string) => {
    try {
      await assessmentsApi.publish(id);
      loadAssessments();
    } catch (err: any) {
      alert('Failed to publish: ' + (err.message || 'Unknown error'));
    }
  };

  const handleArchive = async (id: string) => {
    try {
      await assessmentsApi.archive(id);
      loadAssessments();
    } catch (err: any) {
      alert('Failed to archive: ' + (err.message || 'Unknown error'));
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this assessment?')) {
      return;
    }
    try {
      await assessmentsApi.delete(id);
      loadAssessments();
    } catch (err: any) {
      alert('Failed to delete: ' + (err.message || 'Unknown error'));
    }
  };

  const formatStatus = (status: string): string => {
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (loading) {
    return <div className="loading">Loading assessments...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Assessment Management</h1>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <Link to="/admin/question-banks" className="btn btn-secondary">
            Manage Question Banks
          </Link>
          <Link to="/admin/assessments/new" className="btn btn-primary">
            Create New Assessment
          </Link>
        </div>
      </div>

      <div className="admin-filters">
        <label>
          <span>Search:</span>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search assessments..."
            style={{ marginLeft: '0.5rem', padding: '0.5rem' }}
          />
        </label>
        <label>
          <span>Filter by status:</span>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
        </label>
      </div>

      <div className="assessments-table">
        <table>
          <thead>
            <tr>
              <th>Title</th>
              <th>Status</th>
              <th>Questions</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {assessments.length === 0 ? (
              <tr>
                <td colSpan={5}>No assessments found.</td>
              </tr>
            ) : (
              assessments.map((assessment) => (
                <tr key={assessment.id}>
                  <td>
                    <Link to={`/admin/assessments/${assessment.id}`}>
                      {assessment.title}
                    </Link>
                  </td>
                  <td>
                    <span className={`status-badge status-${assessment.status}`}>
                      {formatStatus(assessment.status)}
                    </span>
                  </td>
                  <td>{assessment.question_count ?? 0}</td>
                  <td>{new Date(assessment.created_at).toLocaleDateString()}</td>
                  <td className="actions">
                    <Link to={`/admin/assessments/${assessment.id}`} className="btn btn-small">
                      Edit
                    </Link>
                    {assessment.status === 'draft' && (
                      <button
                        onClick={() => handlePublish(assessment.id)}
                        className="btn btn-small btn-primary"
                      >
                        Publish
                      </button>
                    )}
                    {assessment.status === 'published' && (
                      <button
                        onClick={() => handleArchive(assessment.id)}
                        className="btn btn-small btn-secondary"
                      >
                        Archive
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(assessment.id)}
                      className="btn btn-small btn-danger"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
